// UserProfileDrawer.tsx - right-edge slide-in drawer that renders
// the admin user profile. Mounted once at the App root. UserLink
// (anywhere a uid appears) opens it via the provider context below.
//
// Why a drawer instead of a route: matches the legacy admin.html UX
// where the underlying surface (e.g. unlock codes list, license-org
// audit) stays in view while you peek at a uid. Reduces the "lose my
// place" cost of every uid click.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useFirestoreQuery } from '@/hooks/useFirestoreQuery';
import {
  getAdminUserProfile,
  type AdminUserProfile,
  type AdminUserProfilePurchase,
} from '@/services/functions/getAdminUserProfile';
import { SkeletonList } from '@/components/SkeletonRow';
import { ErrorState } from '@/components/ErrorState';

// ---------------------------------------------------------------------------
// Context: open(uid) / close() exposed to the whole app.
// ---------------------------------------------------------------------------

interface DrawerContextValue {
  openUid: (uid: string) => void;
}

const UserProfileDrawerContext = createContext<DrawerContextValue | null>(null);

export function useUserProfileDrawer(): DrawerContextValue {
  const ctx = useContext(UserProfileDrawerContext);
  if (!ctx) {
    throw new Error(
      'useUserProfileDrawer must be used inside <UserProfileDrawerProvider>',
    );
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider + Drawer (mounted once at the App root).
// ---------------------------------------------------------------------------

export function UserProfileDrawerProvider({ children }: { children: ReactNode }) {
  const [uid, setUid] = useState<string | null>(null);

  const openUid = useCallback((u: string) => setUid(u), []);
  const close = useCallback(() => setUid(null), []);

  return (
    <UserProfileDrawerContext.Provider value={{ openUid }}>
      {children}
      {uid && <UserProfileDrawer uid={uid} onClose={close} />}
    </UserProfileDrawerContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Drawer body.
// ---------------------------------------------------------------------------

function UserProfileDrawer({
  uid,
  onClose,
}: {
  uid: string;
  onClose: () => void;
}) {
  // Esc closes. Drag-to-dismiss isn't supported - this is a destination
  // surface, not a transient prompt.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Only treat a click as "backdrop dismiss" when BOTH mousedown and
  // mouseup landed on the backdrop. Otherwise a drag-select that
  // started inside the drawer and released on the scrim would close
  // it (same fix as Modal.tsx).
  const downOnBackdropRef = useRef(false);

  const fetcher = useCallback(() => getAdminUserProfile(uid), [uid]);
  const { data, loading, error } = useFirestoreQuery(
    `adminUserProfile:${uid}`,
    fetcher,
  );

  return createPortal(
    <div
      className="console-drawer-backdrop"
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        const closeIt =
          downOnBackdropRef.current && e.target === e.currentTarget;
        downOnBackdropRef.current = false;
        if (closeIt) onClose();
      }}
    >
      <aside
        className="console-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="User profile"
      >
        <header className="console-drawer-header">
          <span style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
            User profile
          </span>
          <button
            type="button"
            className="console-btn console-btn-ghost"
            onClick={onClose}
            aria-label="Close"
          >
            <span className="material-icons">close</span>
          </button>
        </header>
        <div className="console-drawer-body">
          {loading && !data && <SkeletonList count={5} />}
          {error && <ErrorState message={error.message} />}
          {data && <UserProfileCard profile={data} />}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Card body. Same shape as the legacy admin's drawer.
// ---------------------------------------------------------------------------

function countryFlagEmoji(code: string | null): string {
  if (!code || code.length !== 2) return code ?? '';
  const upper = code.toUpperCase();
  const a = upper.charCodeAt(0);
  const b = upper.charCodeAt(1);
  if (a < 65 || a > 90 || b < 65 || b > 90) return upper;
  return (
    String.fromCodePoint(0x1f1e6 + (a - 65)) +
    String.fromCodePoint(0x1f1e6 + (b - 65))
  );
}

function UserProfileCard({ profile }: { profile: AdminUserProfile }) {
  const initial =
    (profile.displayName || profile.email || profile.uid || '?')
      .charAt(0)
      .toUpperCase();

  return (
    <div>
      {profile.bannerUrl && (
        <div
          style={{
            height: 140,
            margin: '-16px -16px 0',
            background: `url("${profile.bannerUrl}") center/cover no-repeat`,
          }}
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          paddingTop: profile.bannerUrl ? 0 : 8,
        }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            marginTop: profile.bannerUrl ? -56 : 0,
            borderRadius: '50%',
            background: 'var(--bg-card)',
            border: '3px solid var(--bg-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            fontWeight: 700,
            color: 'var(--text-primary)',
            overflow: 'hidden',
          }}
        >
          {profile.avatarUrl ? (
            <img
              src={profile.avatarUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            initial
          )}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, textAlign: 'center' }}>
          {profile.displayName ?? profile.email ?? profile.uid.slice(0, 16)}
          {profile.countryCode && (
            <span style={{ marginLeft: 8 }}>
              {countryFlagEmoji(profile.countryCode)}
            </span>
          )}
        </div>
        {profile.email && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            {profile.email}
          </div>
        )}
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-muted)',
            fontFamily: 'JetBrains Mono, monospace',
            wordBreak: 'break-all',
            textAlign: 'center',
            padding: '0 8px',
          }}
        >
          {profile.uid}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginTop: 4,
          }}
        >
          <span
            className={`console-status ${profile.isPaying ? 'is-active' : 'is-neutral'}`}
          >
            {profile.isPaying ? 'Paying' : 'Free'}
          </span>
          {profile.isAnonymous && (
            <span className="console-status is-suspended">Anonymous</span>
          )}
          {profile.isDeleted && (
            <span className="console-status is-expired">Deleted</span>
          )}
          {profile.countryCode && (
            <span className="console-status is-neutral">
              {countryFlagEmoji(profile.countryCode)} {profile.countryCode}
            </span>
          )}
          {profile.offerCode && (
            <span className="console-status is-active">PROMO</span>
          )}
          {profile.isFamilyShare && (
            <span className="console-status is-neutral">Family Share</span>
          )}
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <SectionTitle>Account</SectionTitle>
        <Meta label="Joined" value={formatDate(profile.createdAt)} />
        {profile.revenueCatId && (
          <Meta label="RevenueCat" value={profile.revenueCatId} mono />
        )}
        <Meta label="Products" value={String(profile.purchases.length)} />
        <Meta
          label="Revenue"
          value={
            profile.revenueLocal != null && profile.currency
              ? `${profile.currency} ${profile.revenueLocal.toFixed(2)}`
              : '-'
          }
          color={profile.revenueLocal && profile.revenueLocal > 0 ? 'success' : undefined}
        />
        {profile.countryCode && (
          <Meta
            label="Country"
            value={`${countryFlagEmoji(profile.countryCode)} ${profile.countryCode}`}
          />
        )}
        {profile.currency && <Meta label="Currency" value={profile.currency} />}
        {profile.priceLocal != null && profile.currency && (
          <Meta
            label="Price (local)"
            value={`${profile.currency} ${profile.priceLocal.toFixed(2)}`}
          />
        )}
        {profile.priceUsd != null && (
          <Meta label="Price (USD)" value={`US$ ${profile.priceUsd.toFixed(2)}`} />
        )}
        {profile.taxPercentage != null && (
          <Meta
            label="Tax"
            value={`${(profile.taxPercentage * 100).toFixed(1)}%`}
          />
        )}
        {profile.commissionPercentage != null && (
          <Meta
            label="Commission"
            value={`${(profile.commissionPercentage * 100).toFixed(1)}%`}
          />
        )}
        {profile.offerCode && (
          <Meta label="Offer code" value={profile.offerCode} mono />
        )}

        <SectionTitle style={{ marginTop: 20 }}>Purchases</SectionTitle>
        {profile.purchases.length === 0 ? (
          <div className="console-empty" style={{ padding: '16px 0' }}>
            <span className="material-icons">shopping_cart</span>
            <div className="console-empty-title">No purchases yet</div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginTop: 8,
            }}
          >
            {profile.purchases.map((p, i) => (
              <PurchaseRow key={`${p.productId}-${i}`} purchase={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionTitle({
  children,
  style,
}: {
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
        marginBottom: 10,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Meta({
  label,
  value,
  mono = false,
  color,
}: {
  label: string;
  value: string;
  mono?: boolean;
  color?: 'success';
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gap: 12,
        fontSize: 12,
        padding: '5px 0',
        borderBottom: '1px solid var(--border-color)',
      }}
    >
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          color:
            color === 'success' ? 'var(--success)' : 'var(--text-primary)',
          fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
          fontWeight: color === 'success' ? 600 : 400,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function PurchaseRow({ purchase }: { purchase: AdminUserProfilePurchase }) {
  return (
    <div
      style={{
        padding: 10,
        border: '1px solid var(--border-color)',
        borderRadius: 8,
        background: 'var(--bg-primary)',
        fontSize: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <code style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {purchase.productId}
        </code>
        <span className="console-status is-neutral">{purchase.status}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {purchase.purchasedAt
          ? `Purchased ${formatDate(purchase.purchasedAt)}`
          : 'Purchase date unknown'}
        {purchase.expiresAt && ` · expires ${formatDate(purchase.expiresAt)}`}
        {' · source '}
        {purchase.source}
      </div>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
